import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminSectionSidebar from '../components/AdminSectionSidebar';
import AdminProducts from './AdminProducts';
import AdminUsers from './AdminUsers';
import AdminReportedProducts from './AdminReportedProducts';
import AdminReportedUsers from './AdminReportedUsers';
import AdminReportedChats from './AdminReportedChats';
import AdminContactMessages from './AdminContactMessages';
import AdminLegalDocuments from './AdminLegalDocuments';
import AdminServiceNotices from './AdminServiceNotices';
import AdminStatistics from './AdminStatistics';
import AdminApplicationSettings from './AdminApplicationSettings';
import AdminIllegalContentNotices from './AdminIllegalContentNotices';
import { useTranslations } from '../translations';
import './Administration.css';

interface AdministrationProps {
  isLoggedIn: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProfile: () => void;
  onNavigateToLocations: () => void;
  onNavigateToMyAnnouncements: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToCreateProduct: () => void;
  onNavigateToMessages: (productId?: string) => void;
  onNavigateToProductDetail: (id: string) => void;
  onNavigateToUserDetails: (id: string, publicName?: string) => void;
  onNavigateToTerms: () => void;
  onNavigateToPrivacy: () => void;
  onNavigateToCookies: () => void;
  onNavigateToAbout: () => void;
  onNavigateToContact: () => void;
  onLogout: () => void;
}

type AdminSection =
  | 'statistics'
  | 'users'
  | 'products'
  | 'reported-products'
  | 'reported-users'
  | 'reported-chats'
  | 'contact-messages'
  | 'illegal-content-notices'
  | 'legal-documents'
  | 'service-notices'
  | 'configuration';

const adminSections = new Set<AdminSection>([
  'statistics',
  'users',
  'products',
  'reported-products',
  'reported-users',
  'reported-chats',
  'contact-messages',
  'illegal-content-notices',
  'legal-documents',
  'service-notices',
  'configuration',
]);

const getInitialAdminSection = (): AdminSection => {
  const section = new URLSearchParams(window.location.search).get('section') as AdminSection | null;
  return section && adminSections.has(section) ? section : 'statistics';
};

const Administration: React.FC<AdministrationProps> = ({
  isLoggedIn,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToProductDetail,
  onNavigateToUserDetails,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
  onLogout,
}) => {
  const t = useTranslations();
  const [activeSection, setActiveSection] = useState<AdminSection>(getInitialAdminSection);

  const handleSectionChange = (section: AdminSection) => {
    setActiveSection(section);
    const url = new URL(window.location.href);
    url.searchParams.set('section', section);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
  };

  return (
    <div className="administration-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={true}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={() => onNavigateToMessages()}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onSettingsClick={onNavigateToSettings}
        onAdministrationClick={() => {}}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="administration-container">
        <AdminSectionSidebar
          activeItem={activeSection}
          onStatisticsClick={() => handleSectionChange('statistics')}
          onUsersClick={() => handleSectionChange('users')}
          onProductsClick={() => handleSectionChange('products')}
          onReportedProductsClick={() => handleSectionChange('reported-products')}
          onReportedUsersClick={() => handleSectionChange('reported-users')}
          onReportedChatsClick={() => handleSectionChange('reported-chats')}
          onContactMessagesClick={() => handleSectionChange('contact-messages')}
          onIllegalContentNoticesClick={() => handleSectionChange('illegal-content-notices')}
          onLegalDocumentsClick={() => handleSectionChange('legal-documents')}
          onServiceNoticesClick={() => handleSectionChange('service-notices')}
          onConfigurationClick={() => handleSectionChange('configuration')}
        />

        <main className="administration-main">
          <header className="administration-header">
            <span className="administration-eyebrow">{t.administration.workspaceEyebrow}</span>
            <h1 className="administration-title">{t.administration.title}</h1>
            <p>{t.administration.workspaceDescription}</p>
          </header>

          <div className="administration-content">
            {activeSection === 'statistics' ? (
              <AdminStatistics />
            ) : activeSection === 'products' ? (
              <AdminProducts
                onNavigateToProductDetail={onNavigateToProductDetail}
              />
            ) : activeSection === 'reported-products' ? (
              <AdminReportedProducts
                onNavigateToProductDetail={onNavigateToProductDetail}
              />
            ) : activeSection === 'reported-users' ? (
              <AdminReportedUsers onNavigateToUserDetails={onNavigateToUserDetails} />
            ) : activeSection === 'reported-chats' ? (
              <AdminReportedChats
                onNavigateToProductDetail={onNavigateToProductDetail}
              />
            ) : activeSection === 'contact-messages' ? (
              <AdminContactMessages />
            ) : activeSection === 'illegal-content-notices' ? (
              <AdminIllegalContentNotices />
            ) : activeSection === 'legal-documents' ? (
              <AdminLegalDocuments />
            ) : activeSection === 'service-notices' ? (
              <AdminServiceNotices />
            ) : activeSection === 'configuration' ? (
              <AdminApplicationSettings />
            ) : (
              <AdminUsers onNavigateToUserDetails={onNavigateToUserDetails} />
            )}
          </div>
        </main>
      </div>

      <Footer
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default Administration;
